import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { ownerInputLineOpen } from '@brunel/shared';
import '@xterm/xterm/css/xterm.css';
import type { Guard } from '../App';
import type { Reader, WorkerTerminalRow, WorkforceSession } from '../lib/data';
import { useTerminalControl } from '../lib/mutations';
import {
  TerminalSequenceBuffer,
  ownerTerminalInputPayload,
  subscribeTerminalRealtime,
  terminalInputResultPayload,
  terminalOutputPayload,
  terminalProcessText,
  terminalSessionChanged,
  terminalTransportText,
  type TerminalRealtimeLease,
  type TerminalTransportState,
} from '../lib/terminal-attachment';
import {
  MANUAL_SHELL_HANDED_BACK, isConfirmedManualShell, manualShellOpenWarning, pinnedRuntimeOf,
  terminalInputAccess,
} from '../lib/worker-terminal-view';
import { superviseCommandTerminal } from '../lib/terminal-supervisor';
import {
  TERMINAL_CUT_REASON, assessPaste, clipboardFailureText, pastePreview, pasteRiskLines,
  terminalPlatform, terminalShortcut, withoutTrailingNewline, type PasteAssessment,
} from '../lib/terminal-clipboard';
import {
  persistentAiStateFromBroadcast, persistentContextLine, terminalAiSessionLines, type PersistentAiSessionView,
} from '../lib/persistent-ai-session-view';
import { startWorkerFreshContext } from '../lib/fresh-context';
import { workerTerminalFeedHot, workerTerminalPollMs } from '../lib/main-command-poll';
import { WorkerSummary } from './WorkerSummary';
import { ConversationalComposer } from './ConversationalComposer';
import { TerminalMenuBar, TerminalMenuList, type TerminalMenu, type TerminalMenuItem } from './TerminalMenuBar';
import '../terminal-tools.css';

/**
 * Secure fallback stays responsive while output is moving, then becomes a cheap safety net.
 * Realtime remains the hot path; an idle terminal must not hold the database in a 600 ms loop.
 */
const FALLBACK_POLL_MIN_MS = 750;
const FALLBACK_POLL_MAX_MS = 5000;
/**
 * How often a Worker's own row is read while its terminal is on screen.
 *
 * The node broadcasts terminal-state the moment the row changes; this interval is only a
 * safety net for a lost state event. It stops while the surface or the tab is hidden,
 * stays on the fast beat while the row is active, and otherwise waits the idle floor.
 * The cadence itself lives in `workerTerminalPollMs`.
 */
const FALLBACK_BATCH_MS = 45;
const FALLBACK_CHUNK = 3500;
/** How long Connecting/Reconnecting must last before the label is shown. Brief Realtime flaps stay invisible. */
const TRANSPORT_GRACE_MS = 1200;
/** Server broadcast ACK is transport-only. Wait briefly for the node's PTY-delivery result. */
const LIVE_INPUT_RESULT_TIMEOUT_MS = 4000;

/** Local-only terminal font sizes. Changing one never touches the PTY beyond the normal fit/resize. */
const FONT_SIZE_DEFAULT = 13;
const FONT_SIZE_MIN = 10;
const FONT_SIZE_MAX = 20;
/** How long a copy confirmation stays on screen. */
const NOTICE_MS = 2500;

/** A Worker's row once it has a terminal: the session id is known from here on. */
type LiveWorkerTerminal = WorkerTerminalRow & { session_id: string };

export function WorkerTerminal({ client, guard, orgId, workerId, workerNumber, session, reader, visible = true }: {
  client: SupabaseClient; guard: Guard; orgId: string; workerId: string; workerNumber: number;
  session: WorkforceSession | null; reader: Reader;
  /**
   * False while mounted but not on screen. The PTY/channel/scrollback stay alive; only
   * database polling is reduced. Any missed output catches up from the shared sequence mark.
   */
  visible?: boolean;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const fitHost = useRef<HTMLDivElement | null>(null);
  const takeControlButton = useRef<HTMLButtonElement | null>(null);
  const terminal = useRef<Terminal | null>(null);
  const fit = useRef<FitAddon | null>(null);
  const realtime = useRef<TerminalRealtimeLease | null>(null);
  const live = useRef(false);
  const sendRef = useRef<(data: string) => void>(() => undefined);
  const resizeRef = useRef<(columns: number, rows: number) => void>(() => undefined);
  const fallbackBuffer = useRef<{ sessionId: string | null; data: string; lineOpen: boolean }>({
    sessionId: null, data: '', lineOpen: false,
  });
  /**
   * #736: every Secure fallback batch for this terminal runs one after another on this chain,
   * so a later batch can never reach the PTY before an earlier one. `fallbackInFlight` counts
   * batches handed to the chain and not yet finished. After a failed write, the bytes queued
   * behind it for the same session are held back (they depended on the lost ones) and the
   * owner is told how much was not sent once the chain drains.
   */
  const fallbackChain = useRef<Promise<void>>(Promise.resolve());
  const fallbackInFlight = useRef(0);
  const fallbackHalt = useRef<{ sessionId: string; unsent: number } | null>(null);
  const [fallbackPaused, setFallbackPaused] = useState(false);
  /** True while this surface left Live on its own (an unconfirmed input) but the shared channel stayed up. */
  const liveDemoted = useRef(false);
  const ownerLineOpen = useRef(false);
  const pendingLiveInputs = useRef(new Map<string, { sessionId: string; timer: number }>());
  const fallbackTimer = useRef<number | null>(null);
  const visibleRef = useRef(visible);
  const attachedSessionId = useRef<string | null>(null);
  const focusWhenReady = useRef(false);
  const sequence = useRef(new TerminalSequenceBuffer());
  const latestObservedSeq = useRef(0);
  const [row, setRow] = useState<LiveWorkerTerminal | null>(null);
  const [transport, setTransport] = useState<TerminalTransportState>('fallback');
  const [shownTransport, setShownTransport] = useState<TerminalTransportState>('fallback');
  const transportRef = useRef<TerminalTransportState>('fallback');
  const [localError, setLocalError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [pastePending, setPastePending] = useState<PasteAssessment | null>(null);
  /** The pending paste came from Compose & send: Enter follows the text once it is sent. */
  const [pasteSubmit, setPasteSubmit] = useState(false);
  const [draft, setDraft] = useState('');
  const [composeSubmitted, setComposeSubmitted] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [fontSize, setFontSize] = useState(FONT_SIZE_DEFAULT);
  const [aiSession, setAiSession] = useState<PersistentAiSessionView | null>(null);
  const [freshBusy, setFreshBusy] = useState(false);
  /** #736: the owner asked to open a terminal on a pinned Worker and has not confirmed yet. */
  const [confirmManualOpen, setConfirmManualOpen] = useState(false);
  const [manualOpenWarningText, setManualOpenWarningText] = useState<string | null>(null);
  const [checkingManualOpen, setCheckingManualOpen] = useState(false);
  /** #736: a manual shell was handed back; shown until another terminal is attached. */
  const [handedBack, setHandedBack] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const detailsRef = useRef<HTMLDetailsElement | null>(null);
  const pasteRef = useRef<(text: string) => void>(() => undefined);
  const copyRef = useRef<(clearAfter: boolean) => void>(() => undefined);
  const platform = useRef(terminalPlatform(globalThis.navigator));
  const aiKeysRef = useRef<{ workforceSessionId: string | null; workerId: string }>({ workforceSessionId: null, workerId });
  const {
    open: openTerminal,
    write: writeTerminal,
    resize: resizeTerminal,
    close: closeTerminal,
    release: releaseTerminalInput,
    busy: terminalControlBusy,
    error: terminalControlError,
  } = useTerminalControl(client);
  const nodeId = session?.node_id ?? null;
  const label = `Worker ${workerNumber}`;
  const rowSessionId = row?.session_id ?? null;
  const inputAccess = row === null ? null : terminalInputAccess(row);
  const inputAccessRef = useRef(inputAccess);
  inputAccessRef.current = inputAccess;
  aiKeysRef.current = { workforceSessionId: row?.workforce_session_id ?? session?.id ?? null, workerId };

  const setTransportState = useCallback((next: TerminalTransportState) => {
    transportRef.current = next;
    setTransport(next);
  }, []);

  // "Connecting" is an internal phase, not a terminal condition. Database fallback is
  // already usable while the shared node channel subscribes, so show that calm truth
  // immediately. Only a real reconnect gets a grace period to avoid visible flicker.
  useEffect(() => {
    if (transport === 'connecting') {
      setShownTransport('fallback');
      return undefined;
    }
    if (transport === 'reconnecting') {
      const timer = window.setTimeout(() => setShownTransport(transport), TRANSPORT_GRACE_MS);
      return () => window.clearTimeout(timer);
    }
    setShownTransport(transport);
    return undefined;
  }, [transport]);

  const refreshSession = useCallback(async () => {
    const result = await reader.workerTerminals(orgId);
    if (!result.ok) return;
    const mine = result.rows.find((item) => item.worker_id === workerId) ?? null;
    setRow(mine !== null && mine.session_id !== null ? (mine as LiveWorkerTerminal) : null);
  }, [reader, orgId, workerId]);

  const sessionPollHot = workerTerminalFeedHot(row?.terminal_status ?? null, row?.task_title ?? null);
  useEffect(() => {
    void refreshSession();
    let timer: number | undefined;
    const arm = () => {
      if (timer !== undefined) window.clearInterval(timer);
      timer = undefined;
      const every = workerTerminalPollMs({
        surfaceVisible: visible,
        visibilityState: document.visibilityState,
        active: sessionPollHot,
      });
      if (every === null) return;
      timer = window.setInterval(() => void refreshSession(), every);
    };
    const onVisibility = () => {
      arm();
      if (document.visibilityState !== 'hidden' && visible) void refreshSession();
    };
    document.addEventListener('visibilitychange', onVisibility);
    arm();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [refreshSession, visible, sessionPollHot]);

  useEffect(() => { visibleRef.current = visible; }, [visible]);

  /** Writes one batch in order and returns how many characters the guarded call accepted. */
  const writeTerminalInputFallback = useCallback(async (sessionId: string, data: string, lineOpen: boolean) => {
    for (let offset = 0; offset < data.length; offset += FALLBACK_CHUNK) {
      const chunk = data.slice(offset, offset + FALLBACK_CHUNK);
      const chunkLineOpen = offset + FALLBACK_CHUNK >= data.length ? lineOpen : true;
      const ok = await writeTerminal(orgId, sessionId, chunk, 'owner_terminal', undefined, chunkLineOpen);
      if (!ok) return offset;
    }
    return data.length;
  }, [writeTerminal, orgId]);

  const flushFallback = useCallback(() => {
    if (fallbackTimer.current !== null) {
      window.clearTimeout(fallbackTimer.current);
      fallbackTimer.current = null;
    }
    const pending = fallbackBuffer.current;
    fallbackBuffer.current = { sessionId: null, data: '', lineOpen: false };
    if (pending.sessionId === null || pending.data === '') return;
    const { sessionId, data, lineOpen } = pending;
    fallbackInFlight.current += 1;
    fallbackChain.current = fallbackChain.current.then(async () => {
      try {
        const halted = fallbackHalt.current;
        if (halted !== null && halted.sessionId === sessionId) {
          // An earlier batch for this session is unresolved; sending this one could skip bytes.
          halted.unsent += data.length;
          return;
        }
        if (halted !== null) fallbackHalt.current = null; // A different terminal session owns this batch.
        const sent = await writeTerminalInputFallback(sessionId, data, lineOpen);
        if (sent < data.length) fallbackHalt.current = { sessionId, unsent: data.length - sent };
      } catch {
        fallbackHalt.current = { sessionId, unsent: data.length };
      } finally {
        fallbackInFlight.current -= 1;
        const halt = fallbackHalt.current;
        if (halt !== null && fallbackInFlight.current === 0 && attachedSessionId.current === halt.sessionId) {
          setFallbackPaused(true);
          setLocalError(`${halt.unsent.toLocaleString('en-US')} character${halt.unsent === 1 ? '' : 's'} of terminal input were not confirmed as accepted. They may have reached the terminal. Later input is paused; inspect the terminal before resuming or retyping.`);
        }
      }
    });
  }, [writeTerminalInputFallback]);

  /** True when no Secure fallback bytes are waiting or being written for this terminal. */
  const fallbackIdle = (sessionId: string) => fallbackInFlight.current === 0
    && fallbackBuffer.current.data === '' && fallbackHalt.current?.sessionId !== sessionId;

  const queueFallbackInput = useCallback((sessionId: string, data: string, lineOpen: boolean, flushNow = false) => {
    if (data === '') return;
    if (fallbackBuffer.current.sessionId !== null && fallbackBuffer.current.sessionId !== sessionId) {
      flushFallback();
    }
    fallbackBuffer.current = {
      sessionId,
      data: fallbackBuffer.current.data + data,
      lineOpen,
    };
    if (flushNow || fallbackBuffer.current.data.length >= FALLBACK_CHUNK) flushFallback();
    else if (fallbackTimer.current === null) {
      fallbackTimer.current = window.setTimeout(flushFallback, FALLBACK_BATCH_MS);
    }
  }, [flushFallback]);

  // Owner/manual terminal input only. Automated runtime turns are submitted by the node's
  // persistent runtime adapter and never call this browser input path.
  const writeTerminalInput = useCallback((data: string) => {
    if (!rowSessionId || data === '') return;
    const currentInputAccess = inputAccessRef.current;
    if (currentInputAccess?.writable !== true) {
      setLocalError(currentInputAccess?.explanation ?? 'Terminal input is not available.');
      return;
    }
    const inputSessionId = rowSessionId;
    if (fallbackHalt.current?.sessionId === inputSessionId) {
      setLocalError('Terminal input is paused after an unconfirmed write. Inspect the terminal, then choose Resume input.');
      return;
    }
    const lineOpen = ownerInputLineOpen(ownerLineOpen.current, data);
    ownerLineOpen.current = lineOpen;
    setLocalError(null);
    // While earlier bytes are still on the Secure fallback chain, later bytes follow them
    // there even if Live is back, so a keystroke can never overtake one typed before it.
    if (live.current && realtime.current && fallbackIdle(inputSessionId)) {
      const inputId = globalThis.crypto.randomUUID();
      const timer = window.setTimeout(() => {
        if (!pendingLiveInputs.current.delete(inputId)) return;
        if (attachedSessionId.current !== inputSessionId) return;
        // No application result is ambiguous: the node may have written the bytes and lost
        // only the result broadcast. Never replay an ambiguous input through the DB path.
        fallbackHalt.current = { sessionId: inputSessionId, unsent: 0 };
        setFallbackPaused(true);
        liveDemoted.current = true;
        live.current = false;
        setTransportState('fallback');
        setLocalError('Live input delivery was not confirmed by the node. It was not replayed to avoid duplicate keystrokes. Further input is paused until you inspect the terminal and choose Resume input.');
      }, LIVE_INPUT_RESULT_TIMEOUT_MS);
      pendingLiveInputs.current.set(inputId, { sessionId: inputSessionId, timer });
      void realtime.current.send('terminal-input', ownerTerminalInputPayload(inputSessionId, data, inputId));
      return;
    }
    queueFallbackInput(inputSessionId, data, lineOpen);
  }, [rowSessionId, queueFallbackInput]);

  const sendResize = useCallback((columns: number, rows: number) => {
    if (!rowSessionId || columns < 20 || rows < 5) return;
    if (live.current && realtime.current) {
      void realtime.current.send('terminal-resize', { sessionId: rowSessionId, columns, rows })
        .then((acknowledged) => {
          // Resize is idempotent, so retrying the desired dimensions through the guarded
          // database path is safe even when the Realtime acknowledgement was lost.
          if (!acknowledged) void resizeTerminal(orgId, rowSessionId, columns, rows);
        });
    } else {
      void resizeTerminal(orgId, rowSessionId, columns, rows);
    }
  }, [resizeTerminal, orgId, rowSessionId]);

  useEffect(() => { sendRef.current = writeTerminalInput; }, [writeTerminalInput]);
  useEffect(() => {
    if (!rowSessionId || !focusWhenReady.current) return;
    focusWhenReady.current = false;
    window.requestAnimationFrame(() => terminal.current?.focus());
  }, [rowSessionId]);
  useEffect(() => {
    resizeRef.current = sendResize;
    // Initial fit precedes row attachment. Send measured dimensions to the existing PTY too.
    if (rowSessionId && terminal.current) sendResize(terminal.current.cols, terminal.current.rows);
  }, [sendResize, rowSessionId]);
  const refreshSessionRef = useRef(refreshSession);
  const flushFallbackRef = useRef(flushFallback);
  const readerRef = useRef(reader);
  refreshSessionRef.current = refreshSession;
  flushFallbackRef.current = flushFallback;
  readerRef.current = reader;

  useEffect(() => {
    if (!host.current || !fitHost.current) return;
    const term = new Terminal({
      cursorBlink: true,
      scrollback: 10000,
      fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
      fontSize: 13,
      convertEol: false,
      scrollOnUserInput: true,
      screenReaderMode: true,
      rightClickSelectsWord: false,
    });
    const fitter = new FitAddon();
    term.loadAddon(fitter);
    term.open(fitHost.current);
    if (term.textarea) term.textarea.setAttribute('aria-label', `${label} terminal input`);
    // Tab remains a terminal key; this explicit shortcut reaches the Worker's controls.
    term.attachCustomKeyEventHandler((event) => {
      if (event.type === 'keydown' && event.ctrlKey && event.shiftKey && event.key === 'F6') {
        const workspace = host.current?.closest('.terminal-workspace');
        const control = workspace?.querySelector<HTMLButtonElement>('.terminal-workspace-heading button')
          ?? workspace?.closest('li')?.querySelector<HTMLButtonElement>('.collapsed-worker-card button');
        control?.focus();
        event.preventDefault();
        return false;
      }
      // Copy and paste chords. Plain Ctrl+C with nothing selected stays the interrupt it
      // always was; Tab, arrows and Enter never reach this branch.
      const shortcut = terminalShortcut(event, platform.current, term.hasSelection());
      if (shortcut === 'copy') {
        event.preventDefault();
        copyRef.current(true);
        return false;
      }
      // Let the browser raise its paste event (handled below, through the same confirmation
      // and guarded input path); only keep the chord itself from being typed.
      if (shortcut === 'native_paste' || shortcut === 'swallow') return false;
      return true;
    });
    // Every paste - Cmd+V, Ctrl+Shift+V, the browser's own Paste - is caught before xterm's
    // own handler so it goes through one decision: shown first if it could run commands,
    // then xterm paste() -> onData -> the guarded owner input path. Never around it.
    const onPaste = (event: ClipboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      pasteRef.current(event.clipboardData?.getData('text/plain') ?? '');
    };
    const pasteHost = host.current;
    pasteHost.addEventListener('paste', onPaste, true);
    const selectionDisposable = term.onSelectionChange(() => setHasSelection(term.hasSelection()));
    terminal.current = term;
    fit.current = fitter;
    const dataDisposable = term.onData((data) => sendRef.current(data));
    const sizeDisposable = term.onResize(({ cols, rows }) => resizeRef.current(cols, rows));

    const resize = () => {
      const element = host.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      if (rect.width < 120 || rect.height < 60) return;
      fitter.fit();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(host.current);

    return () => {
      observer.disconnect();
      pasteHost.removeEventListener('paste', onPaste, true);
      selectionDisposable.dispose();
      dataDisposable.dispose();
      sizeDisposable.dispose();
      term.dispose();
      terminal.current = null;
      fit.current = null;
    };
  }, []);

  useEffect(() => {
    const term = terminal.current;
    if (!term) return;

    // Clearing is a terminal-session transition, never a transport transition.
    if (!row) {
      live.current = false;
      realtime.current = null;
      setTransportState('fallback');
      return;
    }
    if (terminalSessionChanged(attachedSessionId.current, row.session_id)) {
      term.reset();
      sequence.current.reset();
      latestObservedSeq.current = 0;
      ownerLineOpen.current = false;
      for (const pendingInput of pendingLiveInputs.current.values()) window.clearTimeout(pendingInput.timer);
      pendingLiveInputs.current.clear();
      attachedSessionId.current = row.session_id;
      liveDemoted.current = false;
      setFallbackPaused(fallbackHalt.current?.sessionId === row.session_id);
      setPastePending(null);
      setPasteSubmit(false);
      setDraft('');
      setComposeSubmitted(false);
      setConfirmManualOpen(false);
      setAiSession(null);
      setHandedBack(false);
    }

    let disposed = false;
    let pollTimer: number | null = null;
    let fallbackPollMs = FALLBACK_POLL_MIN_MS;
    let replayInFlight: Promise<void> | null = null;
    let activeLease: TerminalRealtimeLease | null = null;
    /** What the shared channel itself last reported, independent of this surface's own demotion. */
    let channelLive = false;

    const writeReady = (ready: readonly { seq: number; chunk: string }[]) => {
      for (const item of ready) term.write(item.chunk);
    };

    const replay = (): Promise<void> => {
      if (replayInFlight !== null) return replayInFlight;
      let run: Promise<void>;
      let completedRead = false;
      let sawOutput = false;
      run = (async () => {
        // A batch can fill completely. Continue until caught up so fallback does not leave a
        // hidden gap that would later make Realtime output wait forever.
        if (sequence.current.highestRenderedSeq === 0 && !disposed) {
          // #805: attach at the tail; never page a long session in from seq 1.
          const tail = await readerRef.current.terminalOutputTail(orgId, row.session_id);
          if (!tail.ok || disposed) {
            if (!disposed) fallbackPollMs = FALLBACK_POLL_MAX_MS;
            return;
          }
          if (tail.rows.length > 0) {
            completedRead = true;
            sawOutput = true;
            const oldest = Math.min(...tail.rows.map((item) => item.seq));
            for (const item of tail.rows) latestObservedSeq.current = Math.max(latestObservedSeq.current, item.seq);
            sequence.current.startAt(oldest);
            writeReady(sequence.current.acceptMany(tail.rows));
          }
        }
        while (!disposed) {
          const after = sequence.current.highestRenderedSeq;
          const result = await readerRef.current.terminalOutput(orgId, row.session_id, after);
          if (!result.ok || disposed) {
            if (!disposed) fallbackPollMs = FALLBACK_POLL_MAX_MS;
            return;
          }
          completedRead = true;
          sawOutput ||= result.rows.length > 0;
          for (const item of result.rows) latestObservedSeq.current = Math.max(latestObservedSeq.current, item.seq);
          writeReady(sequence.current.acceptMany(result.rows));
          if (result.rows.length < 500 || sequence.current.highestRenderedSeq === after) return;
        }
      })().finally(() => {
        if (completedRead) {
          fallbackPollMs = sawOutput
            ? FALLBACK_POLL_MIN_MS
            : Math.min(FALLBACK_POLL_MAX_MS, fallbackPollMs * 2);
        }
        if (replayInFlight === run) replayInFlight = null;
      });
      replayInFlight = run;
      return run;
    };

    const schedulePoll = (delayMs?: number) => {
      if (disposed) return;
      if (pollTimer !== null) window.clearTimeout(pollTimer);
      const delay = delayMs ?? ((!visibleRef.current || (live.current && !sequence.current.hasGap))
        ? FALLBACK_POLL_MAX_MS
        : fallbackPollMs);
      pollTimer = window.setTimeout(() => {
        pollTimer = null;
        const next = visibleRef.current && (!live.current || sequence.current.hasGap)
          ? replay()
          : Promise.resolve();
        void next.finally(() => schedulePoll());
      }, delay);
    };

    const supervise = (state: TerminalTransportState, reconnectAttempt: number) => {
      const nowMs = Date.now();
      const result = superviseCommandTerminal({
        sessionId: row.session_id,
        hasTerminal: true,
        status: row.terminal_status,
        closedAt: row.closed_at,
        transport: state,
        inputHeld: row.input_held === true,
        inputHolder: row.input_holder,
        latestSeq: latestObservedSeq.current,
        highestRenderedSeq: sequence.current.highestRenderedSeq,
        hasSequenceGap: sequence.current.hasGap,
        ptyAlive: row.closed_at === null && row.terminal_status !== 'error' && row.terminal_status !== 'exited',
        reconnectAttempt,
        nowMs,
      });
      const plans = [...result.heals, result.transportRecover];
      if (plans.some((plan) => plan.action === 'catchup_sequence')) void replay();
      if (plans.some((plan) => plan.action === 'resubscribe'
        || plan.action === 'reconnect_transport'
        || plan.action === 'restart_transport_binding')) activeLease?.recover();
    };

    const lease = row.node_id === null ? null : subscribeTerminalRealtime(client, row.node_id, {
      onTransport: (state, reconnectAttempt) => {
        if (disposed) return;
        channelLive = state === 'live';
        liveDemoted.current = false;
        live.current = state === 'live';
        setTransportState(state);
        // Persisted output can land while Realtime is subscribing, down, or returning.
        // Catch up only after the shared high-water mark; no visible reset or full replay.
        if (state === 'fallback' || state === 'live') void replay();
        if (state === 'fallback') schedulePoll(FALLBACK_POLL_MIN_MS);
        else if (state === 'live') schedulePoll();
        supervise(state, reconnectAttempt);
      },
      onBroadcast: (event, payload) => {
        if (disposed) return;
        if (event === 'terminal-state') {
          void refreshSessionRef.current();
          return;
        }
        if (event === 'persistent-ai-state') {
          const next = persistentAiStateFromBroadcast(payload, aiKeysRef.current, Date.now());
          if (next !== null) setAiSession(next);
          return;
        }
        if (event === 'terminal-input-result') {
          const result = terminalInputResultPayload(payload);
          if (!result || result.sessionId !== row.session_id) return;
          const pendingInput = pendingLiveInputs.current.get(result.inputId);
          if (!pendingInput || pendingInput.sessionId !== result.sessionId) return;
          window.clearTimeout(pendingInput.timer);
          pendingLiveInputs.current.delete(result.inputId);
          if (result.status === 'delivered') {
            setLocalError(null);
            return;
          }
          // The node explicitly promises these bytes did not reach the PTY, so the loss is
          // visible. Do not auto-replay: an owner retype goes through secure DB fallback.
          if (pendingLiveInputs.current.size > 0) {
            // Another Live write may still reach the PTY after this result. Do not let
            // fallback input overtake it while its delivery is unresolved.
            fallbackHalt.current = { sessionId: row.session_id, unsent: 0 };
            setFallbackPaused(true);
          }
          liveDemoted.current = true;
          live.current = false;
          setTransportState('fallback');
          setLocalError(pendingLiveInputs.current.size > 0
            ? 'Live input was refused while earlier input is still unresolved. Inspect the terminal, then choose Resume input before retyping.'
            : 'Live input was not delivered by the node. Secure fallback is active; retype it.');
          return;
        }
        if (event !== 'terminal-output') return;
        const parsed = terminalOutputPayload(payload);
        if (!parsed || parsed.sessionId !== row.session_id) return;
        latestObservedSeq.current = Math.max(latestObservedSeq.current, parsed.seq);
        const before = sequence.current.highestRenderedSeq;
        writeReady(sequence.current.accept(parsed.seq, parsed.chunk));
        if (parsed.seq > before + 1) void replay();
        else if (liveDemoted.current && channelLive && parsed.seq === before + 1
          && pendingLiveInputs.current.size === 0 && fallbackIdle(row.session_id)) {
          // #736: this surface left Live after an unconfirmed input while the shared channel
          // stayed up. In-order node output arriving on that same channel shows it is healthy
          // again. Nothing ambiguous is replayed; only new input uses Live from here on.
          liveDemoted.current = false;
          live.current = true;
          setTransportState('live');
          schedulePoll();
        }
        supervise(transportRef.current, 0);
      },
    });
    activeLease = lease;
    realtime.current = lease;
    if (lease === null) {
      live.current = false;
      setTransportState('fallback');
    }

    void replay().finally(() => schedulePoll());

    return () => {
      disposed = true;
      if (pollTimer !== null) window.clearTimeout(pollTimer);
      flushFallbackRef.current();
      live.current = false;
      if (realtime.current === lease) realtime.current = null;
      lease?.release();
      // Deliberately do not reset xterm/sequence here: row refreshes, collapsed cards and
      // transport changes all pass through cleanup. Only a new session id clears scrollback.
    };
  }, [client, orgId, row?.session_id, row?.node_id, setTransportState]);

  const manualShell = isConfirmedManualShell(row);

  /** #736: on a pinned Worker, opening by hand is said out loud before it happens. */
  const requestOpen = () => {
    if (!nodeId || checkingManualOpen) return;
    setCheckingManualOpen(true);
    void reader.workerAiExecutionProfiles(orgId).then((result) => {
      if (!visibleRef.current) return;
      const pinned = result.ok
        ? pinnedRuntimeOf(result.rows.find((item) => item.worker_id === workerId) ?? null)
        : null;
      const warning = result.ok
        ? manualShellOpenWarning(pinned)
        : 'Brunel could not confirm this Worker\'s runtime setting. A manual shell can block its runtime. Open it only if you intend to hand it back when finished.';
      if (warning === null) open();
      else {
        setManualOpenWarningText(warning);
        setConfirmManualOpen(true);
      }
    }).catch(() => {
      if (!visibleRef.current) return;
      setManualOpenWarningText('Brunel could not confirm this Worker\'s runtime setting. A manual shell can block its runtime. Open it only if you intend to hand it back when finished.');
      setConfirmManualOpen(true);
    }).finally(() => setCheckingManualOpen(false));
  };

  const open = () => {
    if (!nodeId) return;
    setConfirmManualOpen(false);
    setManualOpenWarningText(null);
    setHandedBack(false);
    void guard('post_owner_command', async () => {
      setLocalError(null);
      focusWhenReady.current = true;
      const cols = Math.max(20, terminal.current?.cols ?? 120);
      const rows = Math.max(5, terminal.current?.rows ?? 30);
      const id = await openTerminal({ orgId, nodeId, workerId, label, shell: null, cwd: null, columns: cols, rows });
      if (id) await refreshSession();
      else focusWhenReady.current = false;
    });
  };

  const takeControl = () => {
    if (!row || inputAccess?.canTakeControl !== true) return;
    void guard('post_owner_command', async () => {
      setLocalError(null);
      if (!await releaseTerminalInput(orgId, row.session_id, null, 'the owner took the keyboard')) return;
      await refreshSession();
      window.requestAnimationFrame(() => terminal.current?.focus());
    });
  };

  // #688 Start Fresh Context: the node replaces only the AI conversation, never this terminal.
  const startFreshContext = () => {
    if (session === null || freshBusy) return;
    setFreshBusy(true);
    setLocalError(null);
    void startWorkerFreshContext({
      orgId, sessionId: session.id, guard,
      rpc: (name, args) => client.rpc(name, args),
    }).then((result) => {
      if (result.ok) setNotice(result.message);
      else setLocalError(result.message);
    }).finally(() => setFreshBusy(false));
  };

  const close = () => {
    if (!row) return;
    void guard('post_owner_command', async () => {
      if (await closeTerminal(orgId, row.session_id)) await refreshSession();
    });
  };

  /**
   * #736 Hand back to Brunel: only for a terminal Brunel recorded as opened by hand. It is the
   * same guarded close_terminal call as End session - no new PTY, no direct node route.
   */
  const handBack = () => {
    if (!row || !isConfirmedManualShell(row)) return;
    const sessionId = row.session_id;
    void guard('post_owner_command', async () => {
      setLocalError(null);
      if (!await closeTerminal(orgId, sessionId)) return;
      setHandedBack(true);
      await refreshSession();
    });
  };

  const interrupt = () => {
    if (!row) return;
    const interruptSessionId = row.session_id;
    setLocalError(null);
    if (live.current && realtime.current && fallbackIdle(interruptSessionId)) {
      void realtime.current.send('terminal-interrupt', { sessionId: interruptSessionId })
        .then((acknowledged) => {
          if (acknowledged || attachedSessionId.current !== interruptSessionId) return;
          // Ctrl+C is not idempotent. Do not send a second interrupt merely because the
          // Realtime acknowledgement was lost; the owner can press Interrupt again if needed.
          setLocalError('Live interrupt delivery was not confirmed. Secure fallback is active; press Interrupt again only if the process is still running.');
        });
    } else writeTerminalInput('\u0003');
  };

  useEffect(() => {
    if (notice === null) return undefined;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  /* ---- Edit: copy, paste, selection --------------------------------------------------- */

  /** Copies the terminal's own selection to the system clipboard, and nowhere else. */
  const copySelection = (clearAfter: boolean) => {
    const term = terminal.current;
    const text = term?.getSelection() ?? '';
    if (text === '') {
      setLocalError('Nothing is selected in the terminal. Drag across the output first, or use Select All.');
      return;
    }
    const clipboard = globalThis.navigator?.clipboard;
    if (clipboard?.writeText === undefined) {
      setLocalError(clipboardFailureText('copy', { name: 'unavailable' }));
      return;
    }
    clipboard.writeText(text).then(() => {
      setLocalError(null);
      setNotice(`Copied ${text.length.toLocaleString('en-US')} character${text.length === 1 ? '' : 's'}.`);
      if (clearAfter) term?.clearSelection();
    }, (error: unknown) => setLocalError(clipboardFailureText('copy', error)));
  };
  copyRef.current = copySelection;

  /**
   * The one way pasted text reaches the program: xterm's own paste(), which converts line
   * breaks to Enter and brackets the text when the program asked for bracketed paste, then
   * emits it on onData -> writeTerminalInput. Owner authority, the live ACK, secure fallback
   * and line-open tracking all apply, exactly as for typed keys. An unconfirmed live delivery
   * is never replayed - that rule lives in writeTerminalInput and is not bypassed here.
   */
  const deliverPaste = (text: string, submit = false): boolean => {
    if (text === '') return false;
    const access = inputAccessRef.current;
    const term = terminal.current;
    if (rowSessionId !== null && fallbackHalt.current?.sessionId === rowSessionId) {
      setLocalError('Terminal input is paused after an unconfirmed write. Inspect the terminal, then choose Resume input.');
      return false;
    }
    if (access?.writable !== true || !term) {
      setLocalError(access?.explanation ?? 'Terminal input is not available.');
      return false;
    }
    term.paste(text);
    if (submit && !/[\r\n]$/.test(text)) {
      // Compose & send: the whole text is one paste write; Enter follows as its own write,
      // in order behind it, so a program with bracketed paste on sees the paste end first.
      flushFallback();
      writeTerminalInput('\r');
      flushFallback();
    } else term.focus();
    return true;
  };

  /**
   * Shows anything that could run a command before a byte is sent; sends plain text straight.
   * Returns 'sent', 'confirm' (waiting on the owner) or 'blocked'.
   */
  const requestPaste = (text: string, submit = false): 'sent' | 'confirm' | 'blocked' => {
    if (text === '') {
      setLocalError(submit ? 'There is nothing to send.' : 'The clipboard has no text to paste.');
      return 'blocked';
    }
    const access = inputAccessRef.current;
    if (rowSessionId !== null && fallbackHalt.current?.sessionId === rowSessionId) {
      setLocalError('Terminal input is paused after an unconfirmed write. Inspect the terminal, then choose Resume input.');
      return 'blocked';
    }
    if (access?.writable !== true) {
      setLocalError(access?.explanation ?? 'Terminal input is not available.');
      return 'blocked';
    }
    const assessment = assessPaste(text);
    if (assessment.needsConfirmation) {
      setLocalError(null);
      setPastePending(assessment);
      setPasteSubmit(submit);
      return 'confirm';
    }
    return deliverPaste(text, submit) ? 'sent' : 'blocked';
  };
  pasteRef.current = (text) => { requestPaste(text); };

  /** Compose & send: the draft goes through the same paste decision and owner input path. */
  const sendComposed = (value: string) => {
    if (requestPaste(value, true) === 'sent') {
      // Terminal delivery can remain Unknown after the browser hands bytes to xterm.
      // Keep the draft and block an accidental second Send until the owner checks it.
      setComposeSubmitted(true);
      setNotice('Send requested. Check the terminal before clearing or sending this draft again.');
    }
  };

  /** Menu Paste: reads the clipboard under this click, then the same decision as Cmd+V. */
  const pasteFromClipboard = () => {
    const clipboard = globalThis.navigator?.clipboard;
    if (clipboard?.readText === undefined) {
      setLocalError(clipboardFailureText('paste', { name: 'unavailable' }));
      return;
    }
    clipboard.readText().then((text) => { requestPaste(text); }, (error: unknown) => setLocalError(clipboardFailureText('paste', error)));
  };

  const confirmPaste = (text: string) => {
    // Cleared first: a second click on a stale preview can never send the text twice.
    const submit = pasteSubmit;
    setPastePending(null);
    setPasteSubmit(false);
    if (deliverPaste(text, submit) && submit) {
      setComposeSubmitted(true);
      setNotice('Send requested. Check the terminal before clearing or sending this draft again.');
    }
  };

  const cancelPaste = () => {
    const submit = pasteSubmit;
    setPastePending(null);
    setPasteSubmit(false);
    setNotice(submit ? 'Send cancelled. Nothing was sent; the text is still in the compose box.' : 'Paste cancelled. Nothing was sent.');
    if (!submit) terminal.current?.focus();
  };

  const selectAll = () => { terminal.current?.selectAll(); setHasSelection(terminal.current?.hasSelection() ?? false); };
  const clearSelection = () => { terminal.current?.clearSelection(); setHasSelection(false); };

  /* ---- View: presentation only. None of these touch the PTY or the saved output. ------- */

  const fitToPanel = () => {
    const element = host.current;
    if (!element || !fit.current) return;
    const rect = element.getBoundingClientRect();
    if (rect.width < 120 || rect.height < 60) return;
    // A fit that changes the size reaches the PTY through the normal onResize -> sendResize.
    fit.current.fit();
  };
  const clearScreen = () => {
    // xterm clear(): this window's screen and scrollback only. The PTY keeps running and
    // the saved output stays in Brunel; opening this Worker again shows it again.
    terminal.current?.clear();
    setNotice('Cleared this view. The running program and the saved output are untouched.');
  };
  const scrollToBottom = () => { terminal.current?.scrollToBottom(); };
  const showSessionDetails = () => {
    const details = detailsRef.current;
    if (!details) return;
    details.open = true;
    details.querySelector('summary')?.focus();
  };
  const changeFontSize = (step: number) => {
    const next = Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, fontSize + step));
    if (next === fontSize || !terminal.current) return;
    terminal.current.options.fontSize = next;
    setFontSize(next);
    fitToPanel();
  };

  const writable = inputAccess?.writable === true;
  const pasteDisabledReason = row === null
    ? 'Open the terminal first.'
    : writable ? undefined : inputAccess?.explanation ?? 'Terminal input is not available.';
  const mac = platform.current === 'mac';
  const copyItem: TerminalMenuItem = {
    id: 'copy', label: 'Copy', ariaLabel: 'Copy terminal selection', shortcut: mac ? '⌘C' : 'Ctrl+Shift+C',
    disabled: !hasSelection, title: hasSelection ? undefined : 'Select text in the terminal to copy it.',
    onSelect: () => copySelection(false),
  };
  const pasteItem: TerminalMenuItem = {
    id: 'paste', label: 'Paste…', ariaLabel: 'Paste clipboard into terminal', shortcut: mac ? '⌘V' : 'Ctrl+Shift+V',
    disabled: pasteDisabledReason !== undefined, title: pasteDisabledReason ?? 'Multi-line or command-like text is shown before it is sent.',
    onSelect: pasteFromClipboard,
  };
  const selectAllItem: TerminalMenuItem = {
    id: 'select-all', label: 'Select All', ariaLabel: 'Select all terminal text', disabled: row === null,
    onSelect: selectAll,
  };
  const menus: readonly TerminalMenu[] = [
    {
      id: 'file', label: 'File', items: [
        {
          id: 'open', label: 'Open terminal', ariaLabel: 'Open terminal',
          disabled: row !== null || !nodeId || terminalControlBusy || checkingManualOpen,
          title: row !== null ? 'This Worker already has a live terminal.' : !nodeId ? 'This Worker has no computer to open a terminal on.' : undefined,
          onSelect: requestOpen,
        },
        ...(manualShell ? [{
          id: 'hand-back', label: 'Hand back to Brunel', ariaLabel: 'Hand this manual shell back to Brunel',
          disabled: terminalControlBusy,
          title: 'Ends this manual shell so Brunel\'s normal dispatch can open the Worker\'s runtime terminal.',
          onSelect: handBack,
        }] : []),
        {
          id: 'end', label: 'End session', ariaLabel: 'End terminal session',
          disabled: row === null || terminalControlBusy,
          title: row === null ? 'No terminal is open.' : 'Ends this Worker\'s terminal and the program running in it.',
          onSelect: close,
        },
      ],
    },
    {
      id: 'edit', label: 'Edit', items: [
        { id: 'cut', label: 'Cut', ariaLabel: 'Cut (not available in a terminal)', disabled: true, title: TERMINAL_CUT_REASON, onSelect: () => undefined },
        copyItem,
        pasteItem,
        selectAllItem,
        {
          id: 'clear-selection', label: 'Clear Selection', ariaLabel: 'Clear terminal selection',
          disabled: !hasSelection, title: hasSelection ? undefined : 'Nothing is selected.', onSelect: clearSelection,
        },
      ],
    },
    {
      id: 'view', label: 'View', items: [
        { id: 'fit', label: 'Fit to panel', ariaLabel: 'Fit terminal to panel', disabled: row === null, onSelect: fitToPanel },
        {
          id: 'clear-screen', label: 'Clear screen (this view)', ariaLabel: 'Clear visible terminal screen', disabled: row === null,
          title: 'Clears what this window shows. The running program and the saved output are untouched.', onSelect: clearScreen,
        },
        { id: 'scroll-bottom', label: 'Scroll to bottom', ariaLabel: 'Scroll terminal to bottom', disabled: row === null, onSelect: scrollToBottom },
        { id: 'details', label: 'Session details', ariaLabel: 'Show terminal session details', disabled: row === null, onSelect: showSessionDetails },
        {
          id: 'font-up', label: 'Larger text', ariaLabel: 'Increase terminal font size', disabled: fontSize >= FONT_SIZE_MAX,
          title: `Text size ${fontSize}. This browser only.`, onSelect: () => changeFontSize(1),
        },
        {
          id: 'font-down', label: 'Smaller text', ariaLabel: 'Decrease terminal font size', disabled: fontSize <= FONT_SIZE_MIN,
          title: `Text size ${fontSize}. This browser only.`, onSelect: () => changeFontSize(-1),
        },
      ],
    },
  ];

  const transportText = terminalTransportText(row !== null, shownTransport);
  const processText = row === null && terminalControlBusy
    ? 'Opening'
    : terminalProcessText(row !== null, row?.terminal_status ?? null, row?.closed_at ?? null);

  return (
    <section ref={sectionRef} className="worker-terminal stack" aria-label={`Worker ${workerNumber} terminal`}>
      <TerminalMenuBar label={`${label} terminal menu`} menus={menus} />
      <div className="row spread">
        <span className="row">
          <strong>Terminal</strong>
          <span className="tag">PTY · {processText}</span>
          {row && (
            <small className="muted">
              {row.node_name}
              {transportText === null ? '' : ` · Transport · ${transportText}`}
            </small>
          )}
        </span>
        <span className="row">
          {!row && <button type="button" className="primary" disabled={!nodeId || terminalControlBusy || confirmManualOpen || checkingManualOpen} onClick={requestOpen}>Open terminal</button>}
          {row && inputAccess?.canTakeControl === true && (
            <button ref={takeControlButton} type="button" className="primary"
              disabled={terminalControlBusy} onClick={takeControl}>Take control</button>
          )}
          {row && <button type="button" disabled={terminalControlBusy} onClick={interrupt}>Interrupt</button>}
          {manualShell && (
            <button type="button" className="primary" disabled={terminalControlBusy} onClick={handBack}>Hand back to Brunel</button>
          )}
          {row && <button type="button" className="link" disabled={terminalControlBusy} onClick={close}>End session</button>}
        </span>
      </div>
      {!row && confirmManualOpen && manualOpenWarningText !== null && (
        <div className="row wrap" role="alertdialog" aria-labelledby={`worker-${workerNumber}-manual-open-title`}
          onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); setConfirmManualOpen(false); } }}>
          <strong id={`worker-${workerNumber}-manual-open-title`}>Open a manual shell on {label}?</strong>
          <small>{manualOpenWarningText}</small>
          <button type="button" disabled={terminalControlBusy} onClick={open}>Open manual shell</button>
          <button type="button" className="link" autoFocus onClick={() => setConfirmManualOpen(false)}>Cancel</button>
        </div>
      )}
      {manualShell && (
        <small className="muted" data-testid="worker-manual-shell">
          Manual shell · this is not the pinned runtime.
          {' '}Brunel's dispatch cannot start its runtime on this Worker while it is open. Hand it back to Brunel when you are done.
        </small>
      )}
      {handedBack && <p role="status" className="muted">{MANUAL_SHELL_HANDED_BACK}</p>}
      <div ref={host} hidden={row === null}
        className={`xterm-host${inputAccess?.writable === false ? ' is-input-locked' : ''}`}
        aria-label={`${label} live terminal`} aria-disabled={inputAccess?.writable === false || undefined}
        onClick={() => {
          if (inputAccess?.writable === true) terminal.current?.focus();
          else if (inputAccess?.canTakeControl === true) takeControlButton.current?.focus();
          else setLocalError(inputAccess?.explanation ?? 'Terminal input is not available.');
        }}
        onContextMenu={(event) => {
          // With text selected, a small Copy / Paste / Select All menu. With nothing selected,
          // the browser keeps its own menu. A right click never pastes by itself.
          if (terminal.current?.hasSelection() !== true) return;
          event.preventDefault();
          const box = sectionRef.current?.getBoundingClientRect();
          setContextMenu({ x: event.clientX - (box?.left ?? 0), y: event.clientY - (box?.top ?? 0) });
        }}>
        <div ref={fitHost} className="xterm-fit" />
      </div>
      {contextMenu !== null && (
        <div className="terminal-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
          <TerminalMenuList
            id={`worker-${workerNumber}-terminal-context`}
            label={`${label} terminal selection menu`}
            items={[copyItem, pasteItem, selectAllItem]}
            onClose={(returnFocus) => {
              setContextMenu(null);
              if (returnFocus) terminal.current?.focus();
            }}
          />
        </div>
      )}
      {pastePending !== null && (() => {
        const preview = pastePreview(pastePending.text);
        const bracketed = terminal.current?.modes.bracketedPasteMode === true;
        return (
          <div className="terminal-paste-confirm" role="alertdialog" aria-modal="false"
            aria-labelledby={`worker-${workerNumber}-paste-title`}
            onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); cancelPaste(); } }}>
            <strong id={`worker-${workerNumber}-paste-title`}>{pasteSubmit ? 'Send' : 'Paste'} into {label}?</strong>
            <ul>
              {pasteRiskLines(pastePending, bracketed).map((line) => <li key={line}>{line}</li>)}
              {pasteSubmit && <li>Enter is added unless the text already ends with a line break.</li>}
            </ul>
            <pre className="terminal-paste-preview" aria-label="Paste preview">{preview.shown}</pre>
            {preview.hiddenLines > 0 && <small className="muted">…and {preview.hiddenLines} more line{preview.hiddenLines === 1 ? '' : 's'}.</small>}
            <div className="row">
              <button type="button" className="primary" onClick={() => confirmPaste(pastePending.text)}>{pasteSubmit ? 'Send' : 'Paste'}</button>
              {pastePending.trailingNewline && !pasteSubmit && (
                <button type="button" onClick={() => confirmPaste(withoutTrailingNewline(pastePending.text))}>
                  Paste without the final line break
                </button>
              )}
              <button type="button" autoFocus onClick={cancelPaste}>Cancel</button>
            </div>
          </div>
        );
      })()}
      {row && (
        <ConversationalComposer
          value={draft}
          onChange={(value) => { setDraft(value); setComposeSubmitted(false); }}
          onSend={sendComposed}
          label={`Compose and send to ${label} terminal`}
          placeholder="Compose a message for this terminal"
          sendLabel="Send"
          disabled={!writable}
          sendDisabled={pastePending !== null || composeSubmitted}
          maxLength={16000}
          historyScope={`${orgId}:${workerId}:${row.session_id}`}
          helperText={writable
            ? 'Enter sends the whole text and adds Enter unless it ends with a line break. Shift+Enter adds a line. Type in the terminal itself for arrows, Tab and Ctrl+C.'
            : inputAccess?.explanation ?? 'Terminal input is not available.'}
        />
      )}
      {row && composeSubmitted && (
        <div className="row wrap">
          <small className="muted">Draft kept until you confirm what reached the terminal.</small>
          <button type="button" onClick={() => setComposeSubmitted(false)}>Allow send again</button>
          <button type="button" onClick={() => { setDraft(''); setComposeSubmitted(false); }}>Clear draft</button>
        </div>
      )}
      {row && (
        <small className={`terminal-input-hint${inputAccess?.writable === false ? ' is-input-locked' : ' muted'}`}>
          {inputAccess?.explanation} {inputAccess?.writable === true ? 'Enter, arrows, Tab and Ctrl+C go to this terminal.' : ''}
          {' '}Ctrl+Shift+F6 moves to controls.
        </small>
      )}
      {row && <small className="terminal-session muted" title={row.session_id}>Session · {row.session_id} · {transportText ?? 'Connected'}</small>}
      {row && session !== null && aiSession !== null && aiSession.supported && (
        <div className="row wrap terminal-fresh-context" data-testid="worker-fresh-context">
          {persistentContextLine(aiSession.context) !== null && (
            <small className="muted" data-testid="worker-context-meter">{persistentContextLine(aiSession.context)}</small>
          )}
          <button type="button" className="ghost"
            disabled={freshBusy || aiSession.state === 'busy' || aiSession.state === 'starting'}
            title="Start a fresh AI conversation for this Worker from its durable handoff. The terminal, role, task chain and model stay."
            onClick={startFreshContext}>
            Start fresh context
          </button>
        </div>
      )}
      {row ? (
        <>
          <details ref={detailsRef} className="terminal-details"><summary>Session details</summary>
          <WorkerSummary client={client} orgId={orgId} reader={reader} row={row} transport={shownTransport} />
          <ul className="terminal-ai-session muted" aria-label="AI session">
            {terminalAiSessionLines(aiSession, Date.now()).map((line) => <li key={line}>{line}</li>)}
          </ul>
          </details>
        </>
      ) : (
        <p className="muted"><strong>Terminal not open</strong> · Open a persistent terminal on this Worker's computer when you need one.</p>
      )}
      {(terminalControlError || localError) && (
        <p role="alert">{[terminalControlError, localError].filter((text) => text !== null && text !== '').join(' ')}</p>
      )}
      {fallbackPaused && row && (
        <button type="button" onClick={() => {
          if (fallbackHalt.current?.sessionId !== row.session_id) return;
          fallbackHalt.current = null;
          setFallbackPaused(false);
          setLocalError(null);
        }}>Resume input after inspecting the terminal</button>
      )}
      {notice !== null && <p role="status" className="terminal-notice muted">{notice}</p>}
    </section>
  );
}
